if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton({
          disable: true,
        });
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      isPreorderFeatureAvailable(html) {
        const selectedVariantData = html.querySelector(
          "[data-selected-variant-is-preorder]"
        )?.innerHTML;

        return !!selectedVariantData ? JSON.parse(selectedVariantData) : false;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // Back in Stock handling
          const backInStockButton = document.querySelector(
            ".notify_me--available"
          );
          const backInStockModal = document.getElementById(
            "back-in-stock-popup"
          );

          if (backInStockButton && backInStockModal) {
            if (!variant.available) {
              backInStockButton.classList.remove("hidden");
              // Update the Product URL in the form when variant changes
              const productUrlInput =
                backInStockModal.querySelector("#product--url");
              if (productUrlInput) {
                productUrlInput.value = `${window.location.origin}${productUrl}?variant=${variant.id}`;
              }
            } else {
              backInStockButton.classList.add("hidden");
            }
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("inventory__stock");
          updateSourceFromDestination("Barcode");
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton({
            disable:
              html
                .getElementById(`ProductSubmitButton-${this.sectionId}`)
                ?.hasAttribute("disabled") ?? true,
            text: window.variantStrings.soldOut,
          });

          const isPreorderAccessible = this.isPreorderFeatureAvailable(html);
          if (isPreorderAccessible) {
            this.productForm?.toggleSubmitButton({
              disable: false,
              isPreorder: isPreorderAccessible,
            });
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton({
          disable: true,
          text: window.variantStrings.unavailable,
        });

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }


      // updateMedia method
      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallery = this.querySelector("media-gallery");
        const mediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        const isMobile = window.innerWidth < 750;

        const activeMedia = mediaGallery?.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!activeMedia) return;

        // Get the current layout
        const desktopLayout = mediaGallery.dataset.desktopLayout;
        const mobileLayout = mediaGallery.dataset.mobileLayout || "columns";

        if (isMobile) {
          // Mobile specific handling
          const mainSlider = mediaGallery.querySelector(
            `#GalleryViewer-${this.dataset.section}`
          );
          const thumbnailSlider = mediaGallery.querySelector(
            `#GalleryThumbnails-${this.dataset.section}`
          );

          if (mainSlider) {
            // Update main slider
            const allSlides = mainSlider.querySelectorAll(
              ".product__media-item"
            );
            allSlides.forEach((slide) => slide.classList.remove("is-active"));
            activeMedia.classList.add("is-active");

            // Smooth scroll to active media
            const scrollOptions = {
              left: activeMedia.offsetLeft,
              behavior: "smooth",
            };
            mainSlider.querySelector(".slider").scrollTo(scrollOptions);

            // Update thumbnails if visible
            if (thumbnailSlider && mobileLayout !== "hide") {
              const thumbnail = thumbnailSlider.querySelector(
                `[data-target="${mediaId}"]`
              );
              if (thumbnail) {
                thumbnailSlider.querySelector(".slider").scrollTo({
                  left: thumbnail.offsetLeft,
                  behavior: "smooth",
                });
              }
            }
          }
        } else {
          // Desktop handling
          if (desktopLayout === "stacked" || desktopLayout === "columns") {
            const mediaList = activeMedia.parentElement;
            if (mediaList && activeMedia !== mediaList.firstElementChild) {
              mediaList.insertBefore(activeMedia, mediaList.firstElementChild);
              mediaGallery.setActiveMedia(mediaId, false);
            }
          } else if (desktopLayout.includes("thumbnail")) {
            // For thumbnail layouts - don't change order, just scroll
            mediaGallery.setActiveMedia(mediaId, false);
            this.scrollToVariantImage(variantFeaturedMediaId);
          }
        }

        // Update modal content
        const modalContent = this.productModal?.querySelector(
          ".product-media-modal__content"
        );
        const newModalContent = html.querySelector(
          "product-modal .product-media-modal__content"
        );
        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      // scrollToVariantImage method
      scrollToVariantImage(mediaId) {
        if (!mediaId) return;

        const mediaGallery = this.querySelector("media-gallery");
        if (!mediaGallery) return;

        const targetMedia = mediaGallery.querySelector(
          `[data-media-id="${this.dataset.section}-${mediaId}"]`
        );
        if (!targetMedia) return;

        const thumbnailList = mediaGallery.querySelector(".thumbnail-list");
        const sliderComponent = thumbnailList?.closest("slider-component");

        // Get the layout type
        const desktopLayout = mediaGallery.dataset.desktopLayout;
        const isMobile = window.innerWidth < 750;

        if (!thumbnailList || !sliderComponent) {
          // Handle mobile or non-slider layouts
          if (isMobile) {
            const mainSlider = mediaGallery.querySelector(
              "#GalleryViewer-" + this.dataset.section
            );
            if (mainSlider) {
              const mainMedia = mainSlider.querySelector(
                `[data-media-id="${this.dataset.section}-${mediaId}"]`
              );
              if (mainMedia) {
                mainSlider.scrollTo({
                  left: mainMedia.offsetLeft,
                  behavior: "smooth",
                });
              }
            }
            return;
          }
        }

        // Find the matching thumbnail
        const thumbnail = thumbnailList?.querySelector(
          `[data-target="${this.dataset.section}-${mediaId}"]`
        );
        if (!thumbnail) return;

        // Check if vertical slider (for thumbnail left carousel)
        const isVertical = desktopLayout === "thumbnail_slider_left";

        // Calculate scroll position
        const position = isVertical
          ? thumbnail.offsetTop
          : thumbnail.offsetLeft;

        // Scroll thumbnails
        if (sliderComponent) {
          sliderComponent.setSlidePosition(position);

          // Scroll main media viewer
          const mainSlider = mediaGallery.querySelector(
            "#GalleryViewer-" + this.dataset.section
          );
          if (mainSlider) {
            const mainMedia = mainSlider.querySelector(
              `[data-media-id="${this.dataset.section}-${mediaId}"]`
            );
            if (mainMedia) {
              mainSlider.querySelector(".slider").scrollTo({
                [isVertical ? "top" : "left"]:
                  mainMedia[isVertical ? "offsetTop" : "offsetLeft"],
                behavior: "smooth",
              });
            }
          }
        }
      }

      // End code

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
